import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { ChatAttachment, ChatBackend, ChatConfig, ChatHistoryEntry, ChatMessage, ChatModelOption, LogEntry } from '../types'
import { fetchChatConfig, fetchChatHistory, recordChatHistory, sendChatMessage } from '../storage'
import { useTracker } from '../context/TrackerContext'
import PhotoModal, { type PhotoSize } from './PhotoModal'

const AUTO_PROMPT =
  'Summarise a possible good thing I can do to get better - in one only change / action, simple one - for tomorrow.'
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export default function Chat({ onDone }: { onDone: () => void }) {
  const { logs, settings, userId, profile } = useTracker()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [backend, setBackend] = useState<ChatBackend>('openai')
  const [openAiModel, setOpenAiModel] = useState<string>()
  const [bedrockModel, setBedrockModel] = useState<string>()
  const [config, setConfig] = useState<ChatConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [configError, setConfigError] = useState<string>()
  const [sharedPhotos, setSharedPhotos] = useState<ChatAttachment[]>([])
  const [sharePanelOpen, setSharePanelOpen] = useState(false)
  const [historyIndex, setHistoryIndex] = useState<number | null>(null)
  const [draftInput, setDraftInput] = useState('')
  const scrollAnchor = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [historyLoading, setHistoryLoading] = useState(true)
  const autoPromptSent = useRef(false)
  const isNavigatingHistory = useRef(false)
  const photoLookup = useMemo(() => buildPhotoLookup(logs), [logs])
  const [photoModal, setPhotoModal] = useState<PhotoModalState | null>(null)
  const openAiOptions = config?.openAiModels ?? []
  const bedrockOptions = config?.bedrockModels ?? []
  const bedrockAvailable = bedrockOptions.length > 0
  const effectiveBackend: ChatBackend = backend === 'bedrock' && bedrockAvailable ? 'bedrock' : 'openai'
  const resolvedOpenAiModel = openAiModel || config?.defaultOpenAiModelId || openAiOptions[0]?.id || 'gpt-4o-mini'
  const resolvedBedrockModel = bedrockOptions.length
    ? bedrockModel || config?.defaultBedrockModelId || bedrockOptions[0]?.id
    : undefined
  const bedrockHint = getBedrockHint(resolvedBedrockModel, bedrockOptions, config?.bedrockRegion)
  const openAiSelectOptions = openAiOptions.length
    ? openAiOptions
    : resolvedOpenAiModel
    ? [{ id: resolvedOpenAiModel, label: resolvedOpenAiModel, supportsImages: false }]
    : []
  const selectedOpenAiModel = openAiSelectOptions.find(option => option.id === resolvedOpenAiModel)
  const selectedBedrockModel = resolvedBedrockModel ? bedrockOptions.find(option => option.id === resolvedBedrockModel) : undefined
  const modelSupportsImages = effectiveBackend === 'openai' ? Boolean(selectedOpenAiModel?.supportsImages) : Boolean(selectedBedrockModel?.supportsImages)
  const logsWithPhotos = useMemo(() => logs.filter(log => log.photoPreviewUrl || log.photoDownloadUrl), [logs])
  const promptHistory = useMemo(() => buildPromptHistory(messages), [messages])

  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (!loading && !historyLoading && !configLoading) {
      inputRef.current?.focus()
    }
  }, [loading, historyLoading, configLoading])

  useEffect(() => {
    let cancelled = false
    async function loadHistory() {
      try {
        const entries = await fetchChatHistory(userId)
        if (cancelled) return
        if (entries.length) {
          setMessages(rehydrateHistory(entries))
          autoPromptSent.current = true
        } else {
          setMessages([])
          autoPromptSent.current = false
        }
      } catch (err) {
        console.warn('Failed to load chat history', err)
        if (!cancelled) {
          setMessages([])
          autoPromptSent.current = false
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false)
        }
      }
    }
    loadHistory()
    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    let cancelled = false
    async function loadConfig() {
      setConfigLoading(true)
      try {
        const remote = await fetchChatConfig()
        if (cancelled) return
        if (remote) {
          setConfig(remote)
          setBackend(defaultBackend(remote))
          setOpenAiModel(remote.defaultOpenAiModelId || remote.openAiModels[0]?.id)
          setBedrockModel(remote.defaultBedrockModelId || remote.bedrockModels[0]?.id)
        } else {
          setConfig(null)
        }
        setConfigError(undefined)
      } catch (err) {
        if (!cancelled) {
          setConfig(null)
          setConfigError((err as Error)?.message || 'Unable to load chat configuration. Using defaults.')
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(false)
        }
      }
    }
    loadConfig()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!historyLoading && !loading && !configLoading && !autoPromptSent.current && messages.length === 0) {
      autoPromptSent.current = true
      submitPrompt(AUTO_PROMPT, { showUserBubble: false })
    }
  }, [historyLoading, loading, configLoading, messages.length])

  useEffect(() => {
    if (!modelSupportsImages || logsWithPhotos.length === 0) {
      setSharePanelOpen(false)
    }
  }, [modelSupportsImages, logsWithPhotos.length])

  useEffect(() => {
    setSharedPhotos(prev =>
      prev.filter(photo => !photo.logId || logs.some(log => log.id === photo.logId && (log.photoPreviewUrl || log.photoDownloadUrl))),
    )
  }, [logs])

  function handleBackendSelect(value: string) {
    const next: ChatBackend = value === 'bedrock' ? 'bedrock' : 'openai'
    if (next === 'bedrock' && !bedrockAvailable) {
      setBackend('openai')
      return
    }
    setBackend(next)
    setMessages([])
    setError(undefined)
    setInput('')
    autoPromptSent.current = false
  }

  async function submitPrompt(text: string, options?: { showUserBubble?: boolean }) {
    const trimmed = text.trim()
    if (!trimmed || loading || historyLoading || configLoading) return
    if (sharedPhotos.length > 0 && !modelSupportsImages) {
      setError('Selected model cannot process images. Remove shared photos or choose a model with image support.')
      return
    }
    const showUserBubble = options?.showUserBubble !== false
    const previousHistory = [...messages]
    const startedAt = new Date().toISOString()
    if (showUserBubble) {
      setMessages([...previousHistory, { role: 'user', content: trimmed, timestamp: startedAt }])
    }
    setLoading(true)
    setError(undefined)
    try {
      const attachments = modelSupportsImages ? sharedPhotos : []
      const selection = {
        backend: effectiveBackend,
        openAiModelId: effectiveBackend === 'openai' ? resolvedOpenAiModel : undefined,
        bedrockModelId: effectiveBackend === 'bedrock' ? resolvedBedrockModel || undefined : undefined,
        mode: 'chat' as const,
        attachments,
      }
      const reply = await sendChatMessage(previousHistory, trimmed, { logs, settings, profile }, selection)
      const response = reply?.trim() || 'Sorry, I did not understand that.'
      const replyTimestamp = new Date().toISOString()
      setMessages(prev => [...prev, { role: 'assistant', content: response, timestamp: replyTimestamp }])
      void recordChatHistory(userId, {
        prompt: trimmed,
        promptTimestamp: startedAt,
        reply: response,
        replyTimestamp,
        mode: 'chat',
        backend: selection.backend,
        model: selection.backend === 'openai' ? selection.openAiModelId : selection.bedrockModelId,
      })
    } catch (err) {
      setError((err as Error)?.message || 'Unable to reach the chat service.')
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  async function handleSend(event?: React.FormEvent) {
    event?.preventDefault()
    const trimmed = input.trim()
    if (!trimmed || loading || historyLoading || configLoading) return
    setInput('')
    setDraftInput('')
    setHistoryIndex(null)
    submitPrompt(trimmed)
  }

  function resetConversation() {
    setMessages([])
    setError(undefined)
    setInput('')
    setDraftInput('')
    setHistoryIndex(null)
    autoPromptSent.current = false
    setSharedPhotos([])
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value
    if (isNavigatingHistory.current) {
      isNavigatingHistory.current = false
      setInput(value)
      return
    }
    setInput(value)
    setDraftInput(value)
    setHistoryIndex(null)
  }

  function handleHistoryNavigation(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    if (promptHistory.length === 0) return
    const inputEl = inputRef.current
    if (!inputEl) return
    const selectionStart = inputEl.selectionStart ?? 0
    const selectionEnd = inputEl.selectionEnd ?? 0
    const caretAtStart = selectionStart === 0 && selectionEnd === 0
    const caretAtEnd = selectionStart === input.length && selectionEnd === input.length
    if (event.key === 'ArrowUp' && !caretAtStart) return
    if (event.key === 'ArrowDown' && !caretAtEnd) return
    event.preventDefault()
    let nextIndex = historyIndex
    if (event.key === 'ArrowUp') {
      if (historyIndex === null) {
        setDraftInput(input)
        nextIndex = 0
      } else {
        nextIndex = Math.min(historyIndex + 1, promptHistory.length - 1)
      }
    } else if (historyIndex !== null) {
      nextIndex = historyIndex === 0 ? null : historyIndex - 1
    }
    setHistoryIndex(nextIndex)
    isNavigatingHistory.current = true
    if (nextIndex === null) {
      setInput(draftInput)
    } else {
      setInput(promptHistory[nextIndex])
    }
  }

  function handlePhotoAttachment(request: ChatImageRequest) {
    if (!request.url) {
      return
    }
    const normalized = normalizeAssetUrl(request.url)
    if (!normalized) {
      return
    }
    const relative = stripApiBase(normalized)
    const requestedSize = parseSizeFromText(request.alt) ?? inferSizeFromUrl(normalized)
    const altLogId = findLogIdInText(request.alt)
    const urlLogId = extractLogIdFromUrl(normalized)
    const asset =
      photoLookup.get(normalized) ||
      photoLookup.get(relative) ||
      (altLogId ? photoLookup.get(altLogId) : undefined) ||
      (urlLogId ? photoLookup.get(urlLogId) : undefined)

    if (asset) {
      setPhotoModal({
        smallUrl: asset.smallUrl,
        largeUrl: asset.largeUrl,
        initialSize: requestedSize ?? (asset.smallUrl ? 'small' : 'large'),
      })
    } else {
      setPhotoModal({
        smallUrl: normalized,
        largeUrl: normalized,
        initialSize: requestedSize ?? 'large',
      })
    }
  }

  function handleSharePhotoChoice(log: LogEntry, size: 'small' | 'large') {
    if (!modelSupportsImages) {
      setError('The selected model cannot analyze images. Choose a model with image support (e.g., GPT-5.2).')
      return
    }
    const targetUrl =
      size === 'small'
        ? normalizeAssetUrl(log.photoPreviewUrl) || normalizeAssetUrl(log.photoDownloadUrl)
        : normalizeAssetUrl(log.photoDownloadUrl) || normalizeAssetUrl(log.photoPreviewUrl)
    if (!targetUrl) {
      setError('Selected photo size is not available for this log.')
      return
    }
    const confirmation = window.confirm(
      `Share the ${size === 'small' ? 'preview' : 'original'} photo from "${log.category}" (${formatLogDate(log.date)})? This allows the AI to analyze the image.`,
    )
    if (!confirmation) {
      return
    }
    const id = `${log.id}-${size}`
    const label = `${formatLogDate(log.date)} · ${log.category} (${size === 'small' ? 'preview' : 'original'})`
    const attachment: ChatAttachment = {
      id,
      logId: log.id,
      size,
      url: targetUrl,
      label,
    }
    setSharedPhotos(prev => {
      const filtered = prev.filter(item => item.id !== id)
      return [...filtered, attachment]
    })
    setSharePanelOpen(false)
    setError(undefined)
  }

  function removeSharedPhoto(photoId: string) {
    setSharedPhotos(prev => prev.filter(item => item.id !== photoId))
  }

  return (
    <div className="chat-container">
      <h3>Tracker Chat</h3>
      <p className="muted">
        Ask for logging tips, nutrition ideas, or anything wellness related. The assistant cannot modify your logs but can reference
        general knowledge.
      </p>
      <div className="chat-share-controls">
        <button
          className="button secondary small"
          type="button"
          onClick={() => setSharePanelOpen(prev => !prev)}
          disabled={!modelSupportsImages || logsWithPhotos.length === 0 || configLoading}
        >
          {sharePanelOpen ? 'Hide log photos' : 'Share a log photo'}
        </button>
        {!modelSupportsImages && (
          <span className="chat-hint">Selected model cannot analyze images. Choose GPT-5.2 to share photos.</span>
        )}
      </div>
      {sharePanelOpen && modelSupportsImages && (
        <div className="chat-share-panel">
          {logsWithPhotos.length === 0 ? (
            <div className="chat-share-empty">No logs with photos are available yet.</div>
          ) : (
            logsWithPhotos.slice(0, 20).map(log => (
              <div key={log.id} className="chat-share-log">
                <div className="chat-share-info">
                  <div className="chat-share-title">{log.category}</div>
                  <div className="chat-share-meta">{formatLogDate(log.date)}</div>
                </div>
                <div className="chat-share-actions">
                  <button
                    type="button"
                    disabled={!log.photoPreviewUrl && !log.photoDownloadUrl}
                    onClick={() => handleSharePhotoChoice(log, 'small')}
                  >
                    Share preview
                  </button>
                  <button
                    type="button"
                    disabled={!log.photoDownloadUrl && !log.photoPreviewUrl}
                    onClick={() => handleSharePhotoChoice(log, 'large')}
                  >
                    Share original
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {sharedPhotos.length > 0 && (
        <div className="chat-shared">
          <div className="chat-shared-header">
            <strong>Shared photos</strong>
            <button type="button" className="button secondary small" onClick={() => setSharedPhotos([])}>
              Clear all
            </button>
          </div>
          <p className="chat-hint">These photos will be sent with each message.</p>
          {sharedPhotos.map(photo => (
            <div key={photo.id} className="chat-shared-item">
              <div>
                <div className="chat-shared-title">{photo.label}</div>
                <div className="chat-shared-meta">{photo.size === 'small' ? 'Small preview' : 'Large original'}</div>
              </div>
              <button type="button" className="button secondary small" onClick={() => removeSharedPhoto(photo.id)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="chat-mode">
        <label>
          AI Backend
          <select
            value={effectiveBackend}
            onChange={e => handleBackendSelect(e.target.value)}
            disabled={loading || configLoading}
          >
            <option value="openai">OpenAI</option>
            {bedrockAvailable && <option value="bedrock">Amazon Bedrock</option>}
          </select>
        </label>
        {effectiveBackend === 'openai' && openAiSelectOptions.length > 0 && (
          <label>
            OpenAI Model
            <select
              value={resolvedOpenAiModel}
              onChange={e => setOpenAiModel(e.target.value)}
              disabled={loading || configLoading || openAiOptions.length === 0}
            >
              {openAiSelectOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label || option.id}
                </option>
              ))}
            </select>
          </label>
        )}
        {effectiveBackend === 'bedrock' && bedrockAvailable && (
          <label>
            Bedrock Model
            <select
              value={resolvedBedrockModel}
              onChange={e => setBedrockModel(e.target.value)}
              disabled={loading || configLoading}
            >
              {bedrockOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label || option.id}
                </option>
              ))}
            </select>
            {bedrockHint && <span className="chat-hint">{bedrockHint}</span>}
          </label>
        )}
      </div>
      {(loading || historyLoading || configLoading) && (
        <div className="chat-spinner" role="status" aria-live="polite">
          <div className="spinner-dot" />
          {configLoading ? 'Loading chat settings…' : historyLoading ? 'Loading conversation…' : 'Waiting for Tracker AI…'}
        </div>
      )}
      {configError && <div className="status">{configError}</div>}
      <div className="chat-messages">
        {messages.length === 0 && !loading && <div className="chat-empty">Say hi to start the conversation.</div>}
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-bubble ${msg.role}`}>
            <div className="chat-role">{msg.role === 'user' ? 'You' : 'Tracker AI'}</div>
            <div className="chat-markdown">
              {renderMarkdown(msg.content, `msg-${idx}`, { onImageClick: handlePhotoAttachment })}
            </div>
            {msg.timestamp && <div className="chat-timestamp">{formatTimestamp(msg.timestamp)}</div>}
          </div>
        ))}
        {loading && <div className="chat-bubble assistant">Thinking…</div>}
        <div ref={scrollAnchor} />
      </div>

      {error && <div className="status error">{error}</div>}

      <form className="chat-input" onSubmit={handleSend}>
        <input
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleHistoryNavigation}
          placeholder="Ask anything…"
          disabled={loading || configLoading}
          autoComplete="off"
        />
        <button className="button" type="submit" disabled={!input.trim() || loading || historyLoading || configLoading}>
          {loading ? 'Sending…' : historyLoading ? 'Loading…' : configLoading ? 'Loading…' : 'Send'}
        </button>
      </form>

      <div className="chat-actions">
        <button className="button secondary small" type="button" onClick={resetConversation} disabled={loading || messages.length === 0}>
          Reset conversation
        </button>
        <button className="button small" type="button" onClick={onDone}>
          Back
        </button>
      </div>

      {photoModal && (
        <PhotoModal
          smallUrl={photoModal.smallUrl}
          largeUrl={photoModal.largeUrl}
          initialSize={photoModal.initialSize}
          onClose={() => setPhotoModal(null)}
          title="Chat photo"
        />
      )}
    </div>
  )
}

interface PhotoModalState {
  smallUrl?: string
  largeUrl?: string
  initialSize: PhotoSize
}

interface ChatImageRequest {
  url: string
  alt?: string
}

interface MarkdownOptions {
  onImageClick?: (request: ChatImageRequest) => void
}

function defaultBackend(config?: ChatConfig | null): ChatBackend {
  if (config?.defaultBackend === 'bedrock' && (config?.bedrockModels?.length ?? 0) > 0) {
    return 'bedrock'
  }
  return 'openai'
}

function getBedrockHint(modelId: string | undefined, models: ChatModelOption[], fallbackRegion?: string) {
  if (!modelId) return ''
  const match = models.find(model => model.id === modelId)
  const region = match?.region || fallbackRegion
  return region ? `Runs in ${region}.` : ''
}

function formatLogDate(value?: string) {
  if (!value) return 'Unknown date'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function rehydrateHistory(entries: ChatHistoryEntry[]): ChatMessage[] {
  const chronological = [...entries].reverse()
  const result: ChatMessage[] = []
  for (const entry of chronological) {
    if (entry.prompt) {
      result.push({ role: 'user', content: entry.prompt, timestamp: entry.promptTimestamp || entry.timestamp })
    }
    if (entry.reply) {
      result.push({ role: 'assistant', content: entry.reply, timestamp: entry.replyTimestamp || entry.timestamp })
    }
  }
  return result
}

function buildPromptHistory(messages: ChatMessage[]) {
  const history: string[] = []
  const seen = new Set<string>()
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message.role !== 'user') continue
    const trimmed = message.content.trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    history.push(trimmed)
  }
  return history
}

function renderMarkdown(content: string, keyPrefix: string, options?: MarkdownOptions) {
  const blocks: React.ReactNode[] = []
  const lines = content.split(/\r?\n/)
  let listItems: string[] = []

  const flushList = () => {
    if (listItems.length === 0) return
    const listKey = `${keyPrefix}-list-${blocks.length}`
    blocks.push(
      <ul key={listKey}>
        {listItems.map((item, idx) => (
          <li key={`${listKey}-item-${idx}`}>{renderInline(item, `${listKey}-item-${idx}`, options)}</li>
        ))}
      </ul>,
    )
    listItems = []
  }

  lines.forEach((line, idx) => {
    const trimmed = line.trimEnd()
    if (!trimmed) {
      flushList()
      return
    }
    const listMatch = trimmed.match(/^[-*+]\s+(.*)/)
    if (listMatch) {
      listItems.push(listMatch[1])
      return
    }
    flushList()
    blocks.push(
      <p key={`${keyPrefix}-p-${idx}`}>
        {renderInline(trimmed, `${keyPrefix}-p-${idx}`, options)}
      </p>,
    )
  })
  flushList()

  if (blocks.length === 0) {
    return <p key={`${keyPrefix}-plain`}>{content}</p>
  }
  return blocks
}

function renderInline(text: string, keyPrefix: string, options?: MarkdownOptions) {
  const nodes: React.ReactNode[] = []
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let tokenIndex = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    const token = match[0]
    const key = `${keyPrefix}-inline-${tokenIndex++}`
    if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>)
    } else if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>)
    } else if (token.startsWith('![')) {
      const imageMatch = token.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
      if (imageMatch) {
        const [, altText, rawUrl] = imageMatch
        const resolvedUrl = normalizeAssetUrl(rawUrl) || rawUrl
        nodes.push(
          <button
            key={key}
            type="button"
            className="chat-image-button"
            onClick={() => options?.onImageClick?.({ url: rawUrl, alt: altText })}
          >
            <img src={resolvedUrl} alt={altText || 'Chat photo'} className="chat-inline-image" />
          </button>,
        )
      } else {
        nodes.push(token)
      }
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      if (linkMatch) {
        nodes.push(
          <a key={key} href={linkMatch[2]} target="_blank" rel="noreferrer">
            {linkMatch[1]}
          </a>,
        )
      } else {
        nodes.push(token)
      }
    }
    lastIndex = pattern.lastIndex
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes
}

type PhotoAsset = {
  logId?: string
  smallUrl?: string
  largeUrl?: string
}

function buildPhotoLookup(logs: LogEntry[]) {
  const map = new Map<string, PhotoAsset>()
  logs.forEach(log => {
    if (!log || (!log.photoPreviewUrl && !log.photoDownloadUrl)) {
      return
    }
    const asset: PhotoAsset = {
      logId: log.id,
      smallUrl: normalizeAssetUrl(log.photoPreviewUrl),
      largeUrl: normalizeAssetUrl(log.photoDownloadUrl),
    }
    const keys = [
      log.id,
      log.photoPreviewUrl,
      asset.smallUrl,
      stripApiBase(asset.smallUrl),
      log.photoDownloadUrl,
      asset.largeUrl,
      stripApiBase(asset.largeUrl),
    ]
    keys.forEach(key => {
      if (key) {
        map.set(key, asset)
      }
    })
  })
  return map
}

function normalizeAssetUrl(path?: string) {
  if (!path) {
    return undefined
  }
  if (/^https?:\/\//i.test(path)) {
    return path
  }
  const trimmedBase = API_BASE.replace(/\/+$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return trimmedBase ? `${trimmedBase}${normalizedPath}` : normalizedPath
}

function stripApiBase(url?: string) {
  if (!url) return undefined
  const trimmedBase = API_BASE.replace(/\/+$/, '')
  if (trimmedBase && url.startsWith(trimmedBase)) {
    const remainder = url.slice(trimmedBase.length)
    return remainder || '/'
  }
  return url
}

function parseSizeFromText(text?: string) {
  if (!text) return undefined
  const normalized = text.toLowerCase()
  if (normalized.includes('large') || normalized.includes('full')) {
    return 'large'
  }
  if (normalized.includes('small') || normalized.includes('preview') || normalized.includes('thumb')) {
    return 'small'
  }
  return undefined
}

function inferSizeFromUrl(url: string) {
  const normalized = url.toLowerCase()
  if (normalized.includes('preview') || normalized.includes('small')) {
    return 'small'
  }
  if (normalized.includes('large') || normalized.includes('full')) {
    return 'large'
  }
  return undefined
}

function findLogIdInText(text?: string) {
  if (!text) return undefined
  const match = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(text)
  return match ? match[1] : undefined
}

function extractLogIdFromUrl(url?: string) {
  if (!url) return undefined
  const match = /\/logs\/([^/]+)\//i.exec(url)
  return match ? match[1] : undefined
}

function formatTimestamp(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  })
}
